import { Link as RouterLink } from 'react-router-dom';
import { firmDailyNavSetupUrl } from 'common/config/urls';
import { useFirmScopedPermissions } from 'common/hooks';
import { Alert, Button } from 'components/ui';
import { PrismThemeProvider } from 'theme';
import { DAILY_NAV_SETUP_TEST_IDS } from '../DailyNavSetup.testIds';
import { useDailyNavSetupProgress } from '../useDailyNavSetupProgress';

type DailyNavSetupBannerProps = {
  firmId: number | null;
  firmSlug: string;
};

// Shown until the firm config is saved and every included company is linked.
const DailyNavSetupBanner = ({ firmId, firmSlug }: DailyNavSetupBannerProps) => {
  const { canMutateFirmScopedEntities: canEdit } = useFirmScopedPermissions({ firmId });
  const { isLoading, isComplete, hasFirmConfig, pendingCount } = useDailyNavSetupProgress(canEdit ? firmId : null);

  // The wizard is for firm editors only, so nobody else is pointed at it.
  if (!canEdit || isLoading || isComplete || !firmSlug) {
    return null;
  }

  // Rendered on pages without the Prism theme, which components/ui needs.
  return (
    <PrismThemeProvider>
      <Alert
        severity="info"
        sx={{ mb: 3 }}
        data-testid={DAILY_NAV_SETUP_TEST_IDS.resumeBanner}
        action={
          <Button<typeof RouterLink>
            size="small"
            component={RouterLink}
            to={firmDailyNavSetupUrl(firmSlug)}
            data-testid={DAILY_NAV_SETUP_TEST_IDS.resumeButton}>
            {hasFirmConfig ? 'Resume setup' : 'Start setup'}
          </Button>
        }>
        {hasFirmConfig
          ? `Daily NAV setup isn't finished: ${pendingCount} included compan${pendingCount === 1 ? 'y needs' : 'ies need'} an external company profile.`
          : 'Daily NAV isn’t set up yet. The setup walks through the firm settings and each company step by step.'}
      </Alert>
    </PrismThemeProvider>
  );
};

export default DailyNavSetupBanner;
